import React from 'react';
import { Box, Container, Typography, Grid, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { GitHub, ArrowOutward, Lock } from '@mui/icons-material';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '56px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '56px',
  maxWidth: '720px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '28px',
    '& span': {
      display: 'block',
      marginTop: '8px',
    },
  },
  '& span': { color: theme.palette.text.secondary },
}));

const SubsectionLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  fontWeight: 500,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '14px',
  },
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  padding: '40px',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  marginBottom: '32px',
  position: 'relative',
  animation: `${fadeIn} 0.4s ease-out`,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    padding: '24px',
    marginBottom: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
    borderRadius: '12px',
    marginBottom: '20px',
  },
}));

const FeatureBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 10px',
  borderRadius: '999px',
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  maxWidth: '100%',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '14px',
    fontSize: '0.7rem',
  },
  '& .pulse': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.25rem, 4vw, 2rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  marginBottom: '16px',
  color: theme.palette.text.primary,
  lineHeight: 1.25,
  [theme.breakpoints.down('sm')]: {
    marginBottom: '12px',
  },
}));

const Prose = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  lineHeight: 1.8,
  color: theme.palette.text.secondary,
  marginBottom: '12px',
  '& strong': { color: theme.palette.text.primary, fontWeight: 600 },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9375rem',
    lineHeight: 1.75,
    marginBottom: '10px',
  },
}));

const PaperMeta = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '14px 0',
  marginBottom: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px 0',
    marginBottom: '18px',
  },
}));

const MetaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '14px',
  padding: '4px 0',
  fontSize: '0.9375rem',
  '& .key': {
    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    minWidth: '72px',
    flexShrink: 0,
  },
  '& .val': {
    color: theme.palette.text.primary,
    fontWeight: 500,
    wordBreak: 'break-word',
  },
  '& .dim': {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    fontSize: '0.875rem',
    '& .key': { minWidth: 0 },
    '& .dim': {
      display: 'block',
      marginTop: '2px',
    },
  },
}));

const ResultsTable = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  padding: '16px',
  background: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.03)' : 'rgba(2, 132, 199, 0.02)',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    borderRadius: '10px',
  },
}));

const TableLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: theme.palette.text.secondary,
  marginBottom: '12px',
}));

const TableHead = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 52px 52px 60px',
  gap: '8px',
  paddingBottom: '8px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: theme.palette.text.secondary,
  '& span:not(:first-of-type)': {
    textAlign: 'right',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr) 40px 40px 48px',
    gap: '4px',
    fontSize: '0.62rem',
    letterSpacing: '0.02em',
  },
}));

const TableRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 52px 52px 60px',
  gap: '8px',
  padding: '10px 0',
  borderBottom: `1px dashed ${theme.palette.divider}`,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  '&:last-of-type': { borderBottom: 'none' },
  '& .approach': {
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontFamily: '"Inter", sans-serif',
  },
  '& span:not(:first-of-type)': {
    textAlign: 'right',
  },
  '& .best': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'minmax(0, 1fr) 40px 40px 48px',
    gap: '4px',
    fontSize: '0.7rem',
    padding: '8px 0',
    '& .approach': {
      fontSize: '0.8125rem',
    },
  },
}));

const PaperLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  marginTop: '32px',
  paddingTop: '24px',
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    marginTop: '24px',
    paddingTop: '18px',
    gap: '10px',
  },
}));

const PaperLinkPrimary = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px 20px',
  borderRadius: '10px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.default,
  textDecoration: 'none',
  transition: 'opacity 0.2s ease',
  '&:hover': { opacity: 0.85 },
  [theme.breakpoints.down('sm')]: {
    flex: '1 1 100%',
  },
}));

const PaperLinkGhost = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '12px 20px',
  borderRadius: '10px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  textDecoration: 'none',
  transition: 'border-color 0.2s ease',
  '&:hover': { borderColor: theme.palette.text.primary },
  [theme.breakpoints.down('sm')]: {
    flex: '1 1 100%',
  },
}));

const LiveCard = styled(Box)(({ theme }) => ({
  padding: '28px',
  borderRadius: '16px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  animation: `${fadeIn} 0.4s ease-out`,
  transition: 'border-color 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.down('md')]: {
    padding: '22px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
    borderRadius: '12px',
  },
}));

const LiveCardHead = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  marginBottom: '16px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '12px',
  },
}));

const LiveCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '4px',
  lineHeight: 1.25,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.125rem',
  },
}));

const LiveCardSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  marginBottom: '14px',
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    marginBottom: '12px',
  },
}));

const LiveCardBody = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  lineHeight: 1.75,
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  flexGrow: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    lineHeight: 1.7,
    marginBottom: '16px',
  },
}));

const LiveCardLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: 'auto',
  paddingTop: '16px',
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '14px',
  },
}));

const LiveLinkPrimary = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 18px',
  borderRadius: '10px',
  fontSize: '0.875rem',
  fontWeight: 600,
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.default,
  textDecoration: 'none',
  transition: 'opacity 0.2s ease',
  '&:hover': { opacity: 0.85 },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '12px 18px',
  },
}));

const LiveLinkGhost = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 18px',
  borderRadius: '10px',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  textDecoration: 'none',
  transition: 'border-color 0.2s ease',
  '&:hover': { borderColor: theme.palette.text.primary },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: '12px 18px',
  },
}));

const LiveBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 10px',
  borderRadius: '999px',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  backgroundColor: 'rgba(34, 197, 94, 0.08)',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: '#22c55e',
  maxWidth: '100%',
  minWidth: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: '4px 8px',
  },
  '& .pulse': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.18)',
    flexShrink: 0,
  },
  '& .live-url': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '220px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  '& .live-short': {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inline',
    },
  },
  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    minWidth: 0,
    '&:hover': { textDecoration: 'underline' },
  },
}));

const Card = styled(Box)(({ theme }) => ({
  padding: '28px',
  borderRadius: '14px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'border-color 0.2s ease, transform 0.2s ease',
  animation: `${fadeIn} 0.4s ease-out`,
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-2px)',
    },
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '18px',
    borderRadius: '12px',
  },
}));

const CardHead = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '14px',
  gap: '12px',
});

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '4px',
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.0625rem',
  },
}));

const Period = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
  },
}));

const CardBody = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  lineHeight: 1.7,
  color: theme.palette.text.secondary,
  marginBottom: '20px',
  flexGrow: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    marginBottom: '16px',
  },
}));

const TagRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: 'auto',
});

const Tag = styled(Box)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  padding: '3px 10px',
  borderRadius: '999px',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
}));

const LinkRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  marginTop: '16px',
});

const LinkBtn = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  '&:hover': {
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
}));

const PrivateNote = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  padding: '6px 10px',
  borderRadius: '8px',
  border: `1px dashed ${theme.palette.divider}`,
  marginTop: '16px',
  width: 'fit-content',
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    alignItems: 'flex-start',
    lineHeight: 1.5,
  },
}));

const formatLiveUrl = (url) =>
  url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');

const Projects = () => {
  const liveProjects = [
    {
      title: 'Royal Dastinos',
      subtitle: 'Tours & travel management',
      period: '2025 · ongoing',
      description:
        'Travel platform for curated tours, custom itineraries, and agent workflows. Visitors build or remix trips and collaborate on plans; agents manage tours and bookings on a separate path.',
      tech: ['React', 'Rails', 'Tailwind', 'Python'],
      liveUrl: 'https://www.royaldastinos.com',
      github: 'https://github.com/DMasilva/theatre-tours',
    },
    {
      title: 'Resilient Minds',
      subtitle: 'Mental health services · Moorhead, MN',
      period: '2025 · ongoing',
      description:
        'Site for a Minnesota mental health provider offering ARMHS, therapy, housing stabilization, and crisis resources. Helps patients find services and reach the team across seven counties.',
      tech: ['React', 'React Router'],
      liveUrl: 'https://www.resilientmindsolution.com',
      github: 'https://github.com/DMasilva/resilient-minds-FE',
    },
    {
      title: 'Professional Consulting Groups',
      subtitle: 'Business consulting · Minnesota',
      period: '2025',
      description:
        'Marketing site for a Minnesota consulting firm covering strategy, digital transformation, and operations. Service pages and consultation CTAs built for prospective clients.',
      tech: ['React'],
      liveUrl: 'https://www.pconsultinggroups.com',
      github: null,
    },
    {
      title: 'My USA Journey',
      subtitle: 'Interactive travel tracker',
      period: '2025',
      description:
        'Side project to track state visits on an interactive US map. Click any state to log trips, days spent, cities, and ratings. Deployed on Vercel while practicing React state and map libraries.',
      tech: ['React', 'react-simple-maps', 'Vercel'],
      liveUrl: 'https://my-usa-tour.vercel.app',
      github: 'https://github.com/DMasilva/my-usa-tour',
    },
  ];

  const others = [
    {
      title: 'Moringa Application Portal',
      period: 'Oct 2022 - Jun 2023',
      description:
        'Full-stack admissions portal for application intake and review. Most of the work was on reliable form flows and edge cases, not just CRUD.',
      tech: ['React', 'Ruby on Rails', 'PostgreSQL'],
      github: 'https://github.com/DMasilva/moringatest',
      demo: null,
    },
    {
      title: 'Cloud Support Dashboards',
      period: 'Mar 2024 - Jun 2024',
      description:
        'PowerBI dashboards over Freshservice ticket data. Surfaced workload patterns and recurring issues so the IT team could see the shape of the problem instead of guessing.',
      tech: ['PowerBI', 'Freshservice', 'Data Modeling'],
      github: null,
      demo: null,
      private: true,
      privateReason: 'Internal NDSU dashboards',
    },
    {
      title: 'GitHub practice repos',
      period: '2022 - ongoing',
      description:
        '165+ public repos from bootcamps and self-study: Rails auth labs, React CRUD apps, Selenium testing, and more. Where most of the repetition that made me faster actually happened.',
      tech: ['React', 'Rails', 'JavaScript'],
      github: 'https://github.com/DMasilva',
      demo: null,
    },
  ];

  return (
    <Section id="projects">
      <Container maxWidth="lg">
        <Eyebrow>Selected Work</Eyebrow>
        <SectionHeading>
          Projects.{' '}
          <span>
            Research accepted at ICST 2026, four live sites in production, and
            a long trail of repos where I learned to ship.
          </span>
        </SectionHeading>

        {/* Featured: LLM research */}
        <FeatureCard>
          <FeatureBadge>
            <span className="pulse" />
            <span>Pre-print · Accepted at ICST 2026</span>
          </FeatureBadge>

          <FeatureTitle>
            Developer vs. DSpot vs. ChatGPT: A Comparative Study of JUnit Test
            Amplification
          </FeatureTitle>

          <PaperMeta>
            <MetaRow>
              <span className="key">Authors</span>
              <span className="val">
                David Onyango Owuor, Ajay Jha
                <span className="dim"> · NDSU</span>
              </span>
            </MetaRow>
            <MetaRow>
              <span className="key">Venue</span>
              <span className="val">
                IEEE ICST 2026
                <span className="dim"> · Daejeon, South Korea</span>
              </span>
            </MetaRow>
          </PaperMeta>

          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mt: 1 }}>
            <Grid item xs={12} md={7}>
              <Prose>
                When developers extend ("amplify") existing JUnit tests, how do
                their changes compare to what a classical amplification tool
                (DSpot) and an LLM (ChatGPT) produce on the same starting
                tests? Each approach had been studied on its own, but no one had
                compared all three on the same dataset.
              </Prose>
              <Prose>
                We mined 117 real developer-amplified tests from open-source
                history and looked at what humans actually change: method calls,
                inputs, expected outputs, assertions. Then we re-amplified the
                corresponding originals with DSpot and with ChatGPT, and
                evaluated all three on line, branch, and mutation coverage.
              </Prose>
              <Prose>
                Developers add modest coverage (~0.74% line/branch, 0.90%
                mutation). DSpot beats them across the board. ChatGPT wins on
                raw coverage (line +8.91%, branch +9.74%) but{' '}
                <strong>underperforms DSpot on mutation score</strong> (3.20% vs
                4.44%). It inflates coverage metrics without catching as many
                real bugs. That points to hybrid tools that combine LLM breadth
                with mutation-driven rigor.
              </Prose>
            </Grid>

            <Grid item xs={12} md={5}>
              <ResultsTable>
                <TableLabel>{'// Headline results · mean improvement'}</TableLabel>
                <TableHead>
                  <span>Approach</span>
                  <span>Line</span>
                  <span>Branch</span>
                  <span>Mutation</span>
                </TableHead>
                <TableRow>
                  <span className="approach">Developer</span>
                  <span>0.74%</span>
                  <span>0.74%</span>
                  <span>0.90%</span>
                </TableRow>
                <TableRow>
                  <span className="approach">DSpot</span>
                  <span>5.66%</span>
                  <span>4.13%</span>
                  <span className="best">4.44%</span>
                </TableRow>
                <TableRow>
                  <span className="approach">ChatGPT</span>
                  <span className="best">8.91%</span>
                  <span className="best">9.74%</span>
                  <span>3.20%</span>
                </TableRow>
              </ResultsTable>

              <Box
                sx={{
                  mt: 2,
                  p: { xs: 2, sm: 2.5 },
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: '12px',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'text.secondary',
                    mb: 1.5,
                  }}
                >
                  {'// Stack'}
                </Typography>
                <TagRow>
                  {['Java', 'JUnit', 'DSpot', 'ChatGPT API', 'Mutation Testing'].map(
                    (t) => (
                      <Tag key={t}>{t}</Tag>
                    ),
                  )}
                </TagRow>
              </Box>
            </Grid>
          </Grid>

          <PaperLinks>
            <PaperLinkPrimary
              href="https://hifromajay.github.io/papers/icst2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read pre-print
              <ArrowOutward sx={{ fontSize: '1rem' }} />
            </PaperLinkPrimary>
            <PaperLinkGhost
              href="https://conf.researchr.org/profile/icst-2026/davidonyangoowuor"
              target="_blank"
              rel="noopener noreferrer"
            >
              ICST 2026 profile
              <ArrowOutward sx={{ fontSize: '1rem' }} />
            </PaperLinkGhost>
          </PaperLinks>
        </FeatureCard>

        {/* Live sites */}
        <SubsectionLabel sx={{ mt: 1 }}>{'// Live sites'}</SubsectionLabel>
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
          {liveProjects.map((project) => {
            const liveHost = formatLiveUrl(project.liveUrl);
            return (
            <Grid item xs={12} md={6} key={project.title}>
              <LiveCard>
                <LiveCardHead>
                  <LiveBadge>
                    <span className="pulse" />
                    <span>
                      Live ·{' '}
                      <span className="live-short">site</span>
                      <a
                        className="live-url"
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {liveHost}
                      </a>
                    </span>
                  </LiveBadge>
                  <Period>{project.period}</Period>
                </LiveCardHead>

                <LiveCardTitle>{project.title}</LiveCardTitle>
                <LiveCardSubtitle>{project.subtitle}</LiveCardSubtitle>
                <LiveCardBody>{project.description}</LiveCardBody>

                <TagRow sx={{ mb: 2, mt: 0 }}>
                  {project.tech.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </TagRow>

                <LiveCardLinks>
                  <LiveLinkPrimary
                    href={project.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit site
                    <ArrowOutward sx={{ fontSize: '0.95rem' }} />
                  </LiveLinkPrimary>
                  {project.github && (
                    <LiveLinkGhost
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View source
                      <ArrowOutward sx={{ fontSize: '0.95rem' }} />
                    </LiveLinkGhost>
                  )}
                </LiveCardLinks>
              </LiveCard>
            </Grid>
          );
          })}
        </Grid>

        <SubsectionLabel>{'// Other work'}</SubsectionLabel>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {others.map((p) => (
            <Grid item xs={12} md={4} key={p.title}>
              <Card>
                <CardHead>
                  <Box>
                    <CardTitle>{p.title}</CardTitle>
                    <Period>{p.period}</Period>
                  </Box>
                </CardHead>
                <CardBody>{p.description}</CardBody>
                <TagRow>
                  {p.tech.map((t) => (
                    <Tag key={t}>{t}</Tag>
                  ))}
                </TagRow>
                {p.private ? (
                  <PrivateNote>
                    <Lock sx={{ fontSize: '0.875rem' }} />
                    {p.privateReason}
                  </PrivateNote>
                ) : (
                  (p.github || p.demo) && (
                    <LinkRow>
                      {p.github && (
                        <LinkBtn
                          href={p.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="GitHub"
                        >
                          <GitHub sx={{ fontSize: '1rem' }} />
                        </LinkBtn>
                      )}
                      {p.demo && (
                        <LinkBtn
                          href={p.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Demo"
                        >
                          <ArrowOutward sx={{ fontSize: '1rem' }} />
                        </LinkBtn>
                      )}
                    </LinkRow>
                  )
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Projects;
